import { loadSave } from "../../gameSave";
import { readLocalSave } from "../../localSave";
import {
  loadLocalSaveIntoState,
  loadServerSaveIntoState,
  resolveConflictKeepLocal,
  resolveConflictKeepServer,
  triggerSave
} from "../persistence";
import { state } from "../state";
import type { ScreenContext } from "../app";
import { renderHeader } from "../components";

// Save comparison (design doc 16.64, Don's direction): lets the captain
// actually SEE both saves' timestamp and location before choosing, rather
// than the existing "server wins on conflict" rule being the only
// visibility they get into which save is newer. Read-only until a Load
// button is pressed -- peeking at either save never overwrites live
// state on its own.
//
// **Two modes since 16.69.** Browsing (reached from Options/Main Menu) is
// the original read-only comparison. **Conflict mode** (state.saveConflict
// set, reached automatically when the server rejects a save because
// another session advanced past our version) makes this a real decision
// point rather than a browsing tool: the server's rejected-against copy is
// already in hand, live state is the competing candidate, and the captain
// must pick one. This replaces the old silent server-wins overwrite --
// see persistence.ts's doSave. Still a whole-save choice, never a
// field-level merge (16.69's own scoping).
export function renderSaveCompareScreen(container: HTMLElement, ctx: ScreenContext): void {
  renderHeader(container, ctx);

  const wrap = document.createElement("div");
  container.appendChild(wrap);

  function formatTimestamp(value: number | string | null): string {
    if (value === null) return "never";
    const date = typeof value === "number" ? new Date(value) : new Date(value);
    return Number.isNaN(date.getTime()) ? "unknown" : date.toLocaleString();
  }

  async function render(): Promise<void> {
    const conflict = state.saveConflict;
    if (conflict) {
      renderConflict(conflict);
      return;
    }

    const local = readLocalSave();
    const server = state.authStatus === "authenticated" ? await loadSave() : null;

    wrap.innerHTML = `
      <h1>Compare Saves</h1>
      <p class="dim">
        Pick which save to actually load -- loading either one replaces your current live
        progress with that save's data outright (no merge), same as the existing
        server-wins-on-conflict rule.
      </p>

      <div class="cargo-list">
        <div class="cargo-row">
          <label><span class="cargo-row-name">Local (this browser)</span></label>
          <span class="dim">${local ? `saved ${formatTimestamp(local.savedAt)}` : "no local save"}</span>
          <button type="button" data-load="local" ${local ? "" : "disabled"}>Load Local</button>
        </div>
        <div class="cargo-row">
          <label><span class="cargo-row-name">Server (cloud account)</span></label>
          <span class="dim">
            ${
              state.authStatus !== "authenticated"
                ? "not signed in"
                : server
                  ? `saved ${formatTimestamp(server.updatedAt)}`
                  : "no server save"
            }
          </span>
          <button type="button" data-load="server" ${server ? "" : "disabled"}>Load Server</button>
        </div>
      </div>

      ${!state.currentShip ? `<button type="button" data-back-to-menu>Back to Main Menu</button>` : ""}
    `;

    wrap.querySelector<HTMLButtonElement>("[data-back-to-menu]")?.addEventListener("click", () => {
      ctx.navigate("mainMenu");
    });

    wrap.querySelector<HTMLButtonElement>('button[data-load="local"]')?.addEventListener("click", () => {
      if (!loadLocalSaveIntoState()) return;
      state.lastEvent = "Loaded the local save.";
      triggerSave(ctx);
      ctx.resync();
    });
    wrap.querySelector<HTMLButtonElement>('button[data-load="server"]')?.addEventListener("click", async () => {
      if (!(await loadServerSaveIntoState())) return;
      state.lastEvent = "Loaded the server save.";
      triggerSave(ctx);
      ctx.resync();
    });
  }

  // Conflict mode (16.69). Deliberately offers no "back"/dismiss: an
  // unresolved conflict means the game currently cannot save at all (every
  // retry would 409 against the same stale version), so letting the captain
  // wander off would silently drop all further progress. Both buttons
  // resolve it; there is no third option that leaves it pending.
  function renderConflict(conflict: NonNullable<typeof state.saveConflict>): void {
    const local = readLocalSave();
    const describe = (data: { currentSystemId?: string; shipName?: string | null; credits?: number }) =>
      [
        data.shipName ?? "no ship",
        data.currentSystemId ?? "unknown system",
        `${Math.round(data.credits ?? 0).toLocaleString()} CR`
      ].join(" &middot; ");

    wrap.innerHTML = `
      <h1>Save Conflict</h1>
      <p class="dim">
        This captain was also played in another session, so the server's save has moved on
        from yours. Both versions are shown below -- pick the one to keep. The other is
        overwritten, and nothing is merged.
      </p>

      <div class="cargo-list">
        <div class="cargo-row">
          <label><span class="cargo-row-name">This session</span></label>
          <span class="dim">
            ${describe({
              shipName: state.currentShip?.name ?? null,
              currentSystemId: state.currentSystemId,
              credits: state.credits
            })}${local ? ` &middot; saved ${formatTimestamp(local.savedAt)}` : ""}
          </span>
          <button type="button" data-keep="local">Keep This Session</button>
        </div>
        <div class="cargo-row">
          <label><span class="cargo-row-name">Server (other session)</span></label>
          <span class="dim">
            ${describe(conflict.serverState)} &middot; saved ${formatTimestamp(conflict.serverUpdatedAt)}
          </span>
          <button type="button" data-keep="server">Keep Server Save</button>
        </div>
      </div>
    `;

    wrap.querySelector<HTMLButtonElement>('button[data-keep="local"]')!.addEventListener("click", () => {
      resolveConflictKeepLocal(ctx);
    });
    wrap.querySelector<HTMLButtonElement>('button[data-keep="server"]')!.addEventListener("click", () => {
      resolveConflictKeepServer(ctx);
    });
  }

  render();
}
